import { Clock, Info } from 'lucide-react';
import { useId, type ReactNode } from 'react';
import type { CrossRegion, NetworkKinds, SpokeToSpoke, TopologyKind } from '../../api/types';
import { strings } from '../../strings';
import { kindReasonLabel, rovingKeyDown } from '../lists/helpers';
import { providerLabel } from '../shared';
import { AWS_TGW_ATTACHMENT, firewallFromCost, GCP_NCC_SPOKE, money } from './helpers';

const t = strings.networks.topology;

/** A small diagram of a topology (decorative; the card's words say what it does). */
function MiniDiagram({ kind }: { kind: TopologyKind }) {
  const spokes = [20, 52, 84, 116];
  const hub = kind === 'hub-spoke' || kind === 'micro-segmentation';
  return (
    <svg aria-hidden viewBox="0 0 140 52" className="net-topo-mini" focusable="false">
      {kind === 'mesh' &&
        spokes.flatMap((x, i) =>
          spokes
            .slice(i + 1)
            .map((y) => (
              <path
                key={`${x}-${y}`}
                d={`M${x} 40 Q${(x + y) / 2} ${14 - (y - x) / 8} ${y} 40`}
                className="net-topo-line"
              />
            )),
        )}
      {hub && (
        <>
          <rect x="56" y="4" width="28" height="12" rx="3" className="net-topo-hub" />
          {spokes.map((x) => (
            <line key={x} x1="70" y1="16" x2={x} y2="36" className="net-topo-line" />
          ))}
        </>
      )}
      {spokes.map((x) => (
        <rect key={x} x={x - 12} y="36" width="24" height="10" rx="2" className="net-topo-node" />
      ))}
      {kind === 'micro-segmentation' &&
        spokes.map((x) => <circle key={x} cx={x + 10} cy="32" r="3" className="net-topo-shield" />)}
    </svg>
  );
}

/**
 * The Settings tab's topology cards (spec 11 6.15.12, mock n04): the registry's available topologies as radio cards with a
 * small diagram, their sentence, a fit hint, the fixed-cost line and availability chips for the network's providers; the
 * others as compact rows with their reason. One Tab stop; arrows move and choose.
 */
export function TopologyCards({
  topologies,
  firewalls,
  providers,
  value,
  applied,
  firewallOn,
  readOnly,
  network,
  awsCosts = false,
  gcpCosts = false,
  onChange,
}: {
  topologies: NetworkKinds['topologies'];
  firewalls: NetworkKinds['firewalls'];
  /** The network's providers. */
  providers: readonly string[];
  value: TopologyKind;
  /** The applied topology ("applied" badge). */
  applied: TopologyKind | null;
  /** The firewall is on in the form (hub topologies then cost nothing extra). */
  firewallOn: boolean;
  readOnly: boolean;
  network: string;
  /** The AWS gate is on: the cards add the transit gateway's cost (feature spec 17, phase 4). */
  awsCosts?: boolean;
  /** The GCP gate is on: the cards add the NCC spoke's cost (feature spec 17, phase 6). */
  gcpCosts?: boolean;
  onChange: (kind: TopologyKind) => void;
}) {
  const ids = useId();
  const ordered = [
    ...topologies.filter((x) => x.status === 'available'),
    ...topologies.filter((x) => x.status !== 'available'),
  ];
  const radioId = (i: number) => `${ids}-radio-${i}`;
  const allIds = ordered.map((_, i) => radioId(i));
  const checked = ordered.findIndex((x) => x.id === value);
  const stop = checked >= 0 ? checked : 0;
  const from = firewallFromCost(firewalls, providers);
  const choose = (i: number) => {
    const kind = ordered[i];
    if (!readOnly && kind && kind.status === 'available') onChange(kind.id);
  };
  const cost = (kind: TopologyKind) =>
    kind === 'isolated' || kind === 'mesh'
      ? t.noFixedCost
      : firewallOn
        ? t.firewallAlreadyOn
        : from
          ? t.fromCost(from)
          : t.noFixedCost;
  return (
    <div
      role="radiogroup"
      aria-label={t.cardsLabel}
      aria-readonly={readOnly || undefined}
      className="net-topo-cards"
    >
      {ordered.map((kind, i) => {
        const available = kind.status === 'available';
        const textId = `${ids}-text-${i}`;
        const reasonId = `${ids}-reason-${i}`;
        const chips = providers.filter((p) => kind.byProvider[p]?.status === 'available');
        return (
          <div
            key={kind.id}
            id={radioId(i)}
            role="radio"
            aria-checked={kind.id === value}
            aria-disabled={!available || readOnly || undefined}
            aria-describedby={available ? textId : reasonId}
            tabIndex={i === stop ? 0 : -1}
            className={available ? 'net-topo-card' : 'net-kind-row'}
            data-topology={kind.id}
            onClick={() => choose(i)}
            onKeyDown={(event) => rovingKeyDown(event, allIds, i, choose)}
          >
            <span className="flex items-center gap-2">
              <span aria-hidden className="net-radio-dot" />
              <span className="min-w-0 flex-1 font-semibold">{kind.label}</span>
              {available && applied === kind.id && <span className="badge tone-neutral">{t.applied}</span>}
              {!available && (
                <span className="badge tone-neutral shrink-0">
                  <Clock aria-hidden className="size-3.5" />
                  {kindReasonLabel(kind.status, null, network)}
                </span>
              )}
            </span>
            {available ? (
              <>
                <MiniDiagram kind={kind.id} />
                <span id={textId} className="block text-[12.5px]">
                  <span className="block text-fg-muted">{t.sentence[kind.id]}</span>
                  <span className="mt-1 block font-semibold">{t.fit[kind.id]}</span>
                  <span className="block text-fg-muted">{cost(kind.id)}</span>
                  {awsCosts && providers.includes('aws') && kind.id !== 'isolated' && (
                    <span className="block text-fg-muted" data-testid="aws-tgw-cost">
                      {t.awsTransitGatewayCost(money(AWS_TGW_ATTACHMENT))}
                    </span>
                  )}
                  {gcpCosts && providers.includes('gcp') && kind.id !== 'isolated' && (
                    <span className="block text-fg-muted" data-testid="gcp-ncc-cost">
                      {strings.networks.gcp.nccCost(money(GCP_NCC_SPOKE))}
                    </span>
                  )}
                  {chips.length > 0 && (
                    <span className="sr-only">{t.availability(chips.map(providerLabel).join(', '))}</span>
                  )}
                </span>
                {chips.length > 0 && (
                  <span aria-hidden className="mt-1 flex flex-wrap gap-1">
                    {chips.map((p) => (
                      <span key={p} className="net-topo-chip">
                        {providerLabel(p)}
                      </span>
                    ))}
                  </span>
                )}
              </>
            ) : (
              <span id={reasonId} className="sr-only">
                {kind.reason?.text ?? kindReasonLabel(kind.status, null, network)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** The options under the chosen card: hub-spoke's spoke to spoke, micro-segmentation's note, across regions. */
export function TopologyOptions({
  kind,
  spokeToSpoke,
  crossRegion,
  firewallOn,
  firewallCost,
  firewallRegions,
  approved,
  pending,
  readOnly,
  choiceMissing,
  awsOnly = false,
  gcp = false,
  messages,
  onSpokeToSpoke,
  onCrossRegion,
}: {
  kind: TopologyKind;
  spokeToSpoke: SpokeToSpoke | null;
  crossRegion: CrossRegion;
  firewallOn: boolean;
  firewallCost: string | null;
  firewallRegions: string;
  approved: number;
  pending: number;
  readOnly: boolean;
  choiceMissing: boolean;
  /** The network's only provider is AWS: the transit gateway routes between spokes (no firewall needed). */
  awsOnly?: boolean;
  /**
   * The network has Google Cloud (feature spec 17, phase 6): hub-spoke fixes spoke-to-spoke to blocked and shows Through
   * the hub `aria-disabled` with the `GCP_HUB_TRANSIT_NOT_SUPPORTED` text.
   */
  gcp?: boolean;
  messages: ReactNode;
  onSpokeToSpoke: (value: SpokeToSpoke) => void;
  onCrossRegion: (value: CrossRegion) => void;
}) {
  const ids = useId();
  if (kind === 'isolated') return messages ? <div className="mt-3">{messages}</div> : null;
  const radio = (
    group: string,
    checked: boolean,
    onChange: () => void,
    label: ReactNode,
    help?: ReactNode,
    reason?: { id: string; text: string },
  ) => (
    <label className={`mb-1 flex items-start gap-2 text-sm ${reason ? 'text-fg-muted' : ''}`}>
      <input
        type="radio"
        name={`${ids}-${group}`}
        className="mt-1"
        checked={checked}
        disabled={readOnly}
        aria-disabled={reason ? true : undefined}
        aria-describedby={reason?.id}
        onChange={reason ? () => undefined : onChange}
      />
      <span>
        {label}
        {help && <span className="block text-[12.5px] text-fg-muted">{help}</span>}
        {reason && (
          <span id={reason.id} className="block text-[12.5px]">
            {reason.text} <code className="font-mono text-xs">GCP_HUB_TRANSIT_NOT_SUPPORTED</code>
          </span>
        )}
      </span>
    </label>
  );
  return (
    <div className="mt-3 space-y-3" data-testid="topology-options">
      {kind === 'micro-segmentation' && (
        <p className="callout callout-info">
          <Info aria-hidden className="size-4 shrink-0" />
          <span>{t.microNote(approved, pending)}</span>
        </p>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        {kind === 'hub-spoke' && (
          <fieldset aria-describedby={choiceMissing ? `${ids}-missing` : undefined}>
            <legend className="label">{t.spokeToSpoke}</legend>
            {radio(
              'spokes',
              !gcp && spokeToSpoke === 'via-hub',
              () => onSpokeToSpoke('via-hub'),
              <>
                <span className="font-medium">{awsOnly || gcp ? t.viaHubAws : t.viaHub}</span>
                {gcp ? null : awsOnly ? (
                  <> · {t.viaHubAwsCost}</>
                ) : (
                  !firewallOn && firewallCost && <> · {t.viaHubCost(firewallCost)}</>
                )}
              </>,
              undefined,
              gcp ? { id: `${ids}-gcp-via-hub`, text: strings.networks.gcp.viaHubNotSupported } : undefined,
            )}
            {radio(
              'spokes',
              gcp || spokeToSpoke === 'blocked',
              () => onSpokeToSpoke('blocked'),
              <>
                <span className="font-medium">{t.blocked}</span> · {t.blockedCost}
              </>,
            )}
            {choiceMissing && (
              <p id={`${ids}-missing`} className="text-[12.5px] font-semibold text-warning">
                {t.chooseSpokes}
              </p>
            )}
          </fieldset>
        )}
        {kind === 'micro-segmentation' && (
          <div>
            <p className="label">{t.underneath}</p>
            <p className="text-sm font-medium">
              {firewallOn ? t.underneathFirewall(firewallRegions) : t.underneathDirect}
            </p>
            <p className="text-[12.5px] text-fg-muted">{t.underneathHelp}</p>
          </div>
        )}
        <fieldset>
          <legend className="label">{t.crossRegion}</legend>
          {radio('cross', crossRegion === 'connected', () => onCrossRegion('connected'), t.connected)}
          {radio('cross', crossRegion === 'separate', () => onCrossRegion('separate'), t.separate)}
        </fieldset>
      </div>
      {messages}
    </div>
  );
}
